'use client';

import { useRef, useEffect, useState } from 'react';
import { usePathname, useParams } from 'next/navigation';
import gsap from 'gsap';
import { CustomEase } from 'gsap/all';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

//* HOC's
import { withUIContext } from '@/context';
import { useUi } from '@weareconceptstudio/core';

function ScrollWrapper({ children, isBurgerMenuOpen, popupIsOpen, winWidth }) {
	const main = useRef();
	const smoother = useRef();
	const [mounted, setMounted] = useState(false);

	const pathname = usePathname();
	const params = useParams();
	const { isMobile } = useUi();

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (smoother.current != null) {
			gsap.to(smoother.current, { scrollTop: 0, duration: 0 });
		}
	}, [pathname, params.lang]);

	useGSAP(
		() => {
			// main.current is null on mobile after mount — skip
			if (!main.current) return;

			const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints;
			const isDesktop = winWidth >= 1280;

			gsap.registerPlugin(ScrollTrigger, ScrollSmoother, CustomEase);

			if (!hasTouchScreen && isDesktop) {
				smoother.current = ScrollSmoother.create({
					smooth: 0.7,
					speed: 1,
					ease: CustomEase.create('custom', 'M0,0 C0.11,0.354 0.134,0.963 1,1 '),
					normalizeScroll: { lockAxis: false, allowNestedScroll: true },
				});
			}
		},
		{ scope: main, dependencies: [pathname] }
	);

	useEffect(() => {
		if (smoother.current != null) {
			smoother.current.paused(isBurgerMenuOpen || popupIsOpen, true);
		}
	}, [isBurgerMenuOpen, popupIsOpen]);

	// SSR-safe: first render (server + client) always renders wrapper → no hydration mismatch
	// After mount: switch to children-only on mobile
	if (mounted && isMobile) {
		return children;
	}

	return (
		<div
			ref={main}
			id='smooth-wrapper'>
			<div id='smooth-content'>{children}</div>
		</div>
	);
}

export default withUIContext(ScrollWrapper, ['isBurgerMenuOpen', 'popupIsOpen', 'winWidth']);
